/**
 * Photos on a poor connection.
 *
 * Photos are ~99% of a shared game's size (about 66 KB each), and the host's phone used to send the whole
 * history — every round's photos — on every update. On a slow connection that stops getting through. So
 * once a send TIMES OUT, the phones stop putting photos inside the game and move them to their own
 * small requests, retried as the connection allows:
 *
 *  - Host -> guests. The host's updates go out without photos, tagged with `photoRounds` (which rounds
 *    have one held back). The host uploads each round's photos separately and remembers which are done.
 *    A guest taps "Get photos" (or Download PDF) to fetch them.
 *  - Guest -> host. A guest's own scan photo is sent after the round instead of with it. The round goes
 *    first, tagged `photosDeferred`; the host, having applied it, waits for the photo and attaches it.
 *
 * Everything here is the bookkeeping for that — pure functions plus small localStorage-backed lists
 * (with an in-memory fallback, since photos are large and storage can refuse them). No network.
 */

import type { RoundRecord } from '../game/types';
import type { SharedGameState } from './gameShare';
import type { StorageLike } from './recognitionAccuracy';

/** The relay's post-End-Game grace period: a guest's link stops working this long after the game ended. */
export const GUEST_LINK_WINDOW_MS = 60 * 60 * 1000;

const defaultStorage = (): StorageLike => localStorage;

// ---------------------------------------------------------------------------
// Which rounds' photos are travelling separately (host)
// ---------------------------------------------------------------------------

/** Rounds that carry at least one photo. */
export function roundsWithPhotos(rounds: readonly RoundRecord[]): RoundRecord[] {
  return rounds.filter((r) => (r.photos?.length ?? 0) > 0);
}

/** The history as the guests' copy carries it once photos travel separately: same rounds, no `photos`. */
export function withoutPhotos(rounds: readonly RoundRecord[]): RoundRecord[] {
  return rounds.map((r) => {
    if (!r.photos) return r;
    const { photos: _held, ...rest } = r;
    return rest;
  });
}

/** Which rounds have a photo held back — what the game state's `photoRounds` says. */
export function photoRoundIndices(rounds: readonly RoundRecord[]): number[] {
  return roundsWithPhotos(rounds).map((r) => r.index);
}

/**
 * What the host publishes as the round history. Normally the rounds as they are; once photos travel
 * separately (`slim`), the same rounds without photos plus the list of rounds whose photos are held back.
 */
export function historyForGuests(
  rounds: readonly RoundRecord[],
  slim: boolean,
): { roundHistory: RoundRecord[]; photoRounds?: number[] } {
  if (!slim) return { roundHistory: [...rounds] };
  return { roundHistory: withoutPhotos(rounds), photoRounds: photoRoundIndices(rounds) };
}

const SLIM_PREFIX = 'mahjong-scorer:slimShare:';

/** True once a send of this game timed out: from then on its photos travel separately. Remembered across reloads. */
export function isSlim(gameId: string, storage: StorageLike = defaultStorage()): boolean {
  try {
    return storage.getItem(SLIM_PREFIX + gameId) === '1';
  } catch {
    return slimInMemory.has(gameId);
  }
}

const slimInMemory = new Set<string>();

export function setSlim(gameId: string, storage: StorageLike = defaultStorage()): void {
  slimInMemory.add(gameId);
  try {
    storage.setItem(SLIM_PREFIX + gameId, '1');
  } catch {
    // stays in memory for this page load
  }
}

export function clearSlim(gameId: string, storage: StorageLike = defaultStorage()): void {
  slimInMemory.delete(gameId);
  try {
    storage.removeItem(SLIM_PREFIX + gameId);
  } catch {
    // nothing more to do
  }
}

const UPLOADED_PREFIX = 'mahjong-scorer:roundPhotosSent:';
const roundKey = (r: Pick<RoundRecord, 'index' | 'recordedAt'>): string => `${r.index}:${r.recordedAt}`;

function readList(storage: StorageLike, key: string): string[] {
  try {
    const raw = storage.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function writeList(storage: StorageLike, key: string, list: string[]): void {
  try {
    storage.setItem(key, JSON.stringify(list));
  } catch {
    // best effort — worst case a photo is sent twice, which is harmless
  }
}

/** Rounds whose photos the host has uploaded for the guests. A round is identified by index AND recordedAt, so a deleted-and-redone round is a different one. */
export function uploadedRoundKeys(gameId: string, storage: StorageLike = defaultStorage()): Set<string> {
  return new Set(readList(storage, UPLOADED_PREFIX + gameId));
}

export function markRoundPhotosUploaded(
  gameId: string,
  round: Pick<RoundRecord, 'index' | 'recordedAt'>,
  storage: StorageLike = defaultStorage(),
): void {
  const list = readList(storage, UPLOADED_PREFIX + gameId);
  const key = roundKey(round);
  if (!list.includes(key)) writeList(storage, UPLOADED_PREFIX + gameId, [...list, key]);
}

/** Rounds with photos the host has not yet uploaded — what is still owed to the guests. */
export function roundsOwingPhotos(
  gameId: string,
  rounds: readonly RoundRecord[],
  storage: StorageLike = defaultStorage(),
): RoundRecord[] {
  const done = uploadedRoundKeys(gameId, storage);
  return roundsWithPhotos(rounds).filter((r) => !done.has(roundKey(r)));
}

// ---------------------------------------------------------------------------
// Photos a guest promised the host (host side)
// ---------------------------------------------------------------------------

export interface AwaitedPhoto {
  submissionId: string;
  /** The round the host recorded from that submission. */
  index: number;
  recordedAt: number;
}

const AWAITED_PREFIX = 'mahjong-scorer:awaitedPhotos:';

export function loadAwaitedPhotos(gameId: string, storage: StorageLike = defaultStorage()): AwaitedPhoto[] {
  try {
    const raw = storage.getItem(AWAITED_PREFIX + gameId);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (a): a is AwaitedPhoto =>
        typeof a === 'object' &&
        a !== null &&
        typeof (a as AwaitedPhoto).submissionId === 'string' &&
        typeof (a as AwaitedPhoto).index === 'number' &&
        typeof (a as AwaitedPhoto).recordedAt === 'number',
    );
  } catch {
    return [];
  }
}

function saveAwaited(gameId: string, list: AwaitedPhoto[], storage: StorageLike): void {
  try {
    storage.setItem(AWAITED_PREFIX + gameId, JSON.stringify(list));
  } catch {
    // best effort
  }
}

/** The host applied a round whose photo is still on its way — remember which round it belongs to. */
export function addAwaitedPhoto(gameId: string, item: AwaitedPhoto, storage: StorageLike = defaultStorage()): void {
  const list = loadAwaitedPhotos(gameId, storage).filter((a) => a.submissionId !== item.submissionId);
  saveAwaited(gameId, [...list, item], storage);
}

export function removeAwaitedPhoto(gameId: string, submissionId: string, storage: StorageLike = defaultStorage()): void {
  saveAwaited(
    gameId,
    loadAwaitedPhotos(gameId, storage).filter((a) => a.submissionId !== submissionId),
    storage,
  );
}

/**
 * Puts a late-arriving photo on the round it belongs to. Only if that exact round is still there —
 * same index AND same `recordedAt` (a round deleted and re-recorded reuses its index, and must not
 * inherit someone else's photo) — and only if it has no photo of its own yet. Returns whether it was attached.
 */
export function attachPhotoToRound(rounds: RoundRecord[], awaited: AwaitedPhoto, photos: string[]): boolean {
  const round = rounds.find((r) => r.index === awaited.index && r.recordedAt === awaited.recordedAt);
  if (!round || (round.photos?.length ?? 0) > 0 || photos.length === 0) return false;
  round.photos = [...photos];
  return true;
}

// ---------------------------------------------------------------------------
// A guest's own photos, waiting to be sent after their rounds (guest side)
// ---------------------------------------------------------------------------

export interface GuestPhotoJob {
  gameId: string;
  submissionId: string;
  photos: string[];
  queuedAt: number;
}

const JOBS_PREFIX = 'mahjong-scorer:guestPhotoJobs:';
/** localStorage may refuse (photos are large): the queue then lives in memory until the page closes. */
const jobMemory = new Map<string, GuestPhotoJob[]>();

function isJob(value: unknown, gameId: string): value is GuestPhotoJob {
  if (typeof value !== 'object' || value === null) return false;
  const j = value as Partial<GuestPhotoJob>;
  return (
    j.gameId === gameId &&
    typeof j.submissionId === 'string' &&
    typeof j.queuedAt === 'number' &&
    Array.isArray(j.photos) &&
    j.photos.length > 0 &&
    j.photos.every((p) => typeof p === 'string')
  );
}

/** Every photo this phone still has to send for a game, oldest first. Several rounds can be waiting at once. */
export function loadGuestPhotoJobs(gameId: string, storage: StorageLike = defaultStorage()): GuestPhotoJob[] {
  try {
    const raw = storage.getItem(JOBS_PREFIX + gameId);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.filter((j): j is GuestPhotoJob => isJob(j, gameId));
    }
  } catch {
    // fall back to memory
  }
  return [...(jobMemory.get(gameId) ?? [])];
}

function storeJobs(gameId: string, jobs: GuestPhotoJob[], storage: StorageLike): void {
  if (jobs.length === 0) {
    jobMemory.delete(gameId);
    try {
      storage.removeItem(JOBS_PREFIX + gameId);
    } catch {
      // nothing more to do
    }
    return;
  }
  jobMemory.set(gameId, jobs);
  try {
    storage.setItem(JOBS_PREFIX + gameId, JSON.stringify(jobs));
    jobMemory.delete(gameId);
  } catch {
    // kept in memory
  }
}

/**
 * Adds a photo to the queue. Each round keeps its own entry (keyed by its submission id), so a second poor round
 * can never replace the first one's photo — that would lose it. Queuing the same submission again replaces only itself.
 */
export function enqueueGuestPhotoJob(job: GuestPhotoJob, storage: StorageLike = defaultStorage()): void {
  const others = loadGuestPhotoJobs(job.gameId, storage).filter((j) => j.submissionId !== job.submissionId);
  storeJobs(job.gameId, [...others, job], storage);
}

/** Takes one sent (or unsendable) photo off the queue. */
export function removeGuestPhotoJob(gameId: string, submissionId: string, storage: StorageLike = defaultStorage()): void {
  storeJobs(
    gameId,
    loadGuestPhotoJobs(gameId, storage).filter((j) => j.submissionId !== submissionId),
    storage,
  );
}

/** Empties the queue (the link is gone, so nothing in it can be sent any more). */
export function clearGuestPhotoJobs(gameId: string, storage: StorageLike = defaultStorage()): void {
  storeJobs(gameId, [], storage);
}

// ---------------------------------------------------------------------------
// "This phone's connection has been slow" (guest side)
// ---------------------------------------------------------------------------

/**
 * How long a phone keeps treating its connection as slow after a send timed out. A hang can be a passing glitch,
 * so it is not forever: after this the next round is tried WITH its photo again. It also ends the moment a photo
 * upload gets through (see `markConnectionFine`), which is the sign the connection has recovered.
 */
export const SLOW_CONNECTION_MEMORY_MS = 10 * 60 * 1000;

const SLOW_PREFIX = 'mahjong-scorer:guestSlowUntil:';
const slowMemory = new Map<string, number>();

/** A send from this phone timed out: for a while, send rounds without their photos straight away instead of waiting out another timeout. */
export function markConnectionSlow(gameId: string, now: number = Date.now(), storage: StorageLike = defaultStorage()): void {
  const until = now + SLOW_CONNECTION_MEMORY_MS;
  slowMemory.set(gameId, until);
  try {
    storage.setItem(SLOW_PREFIX + gameId, String(until));
  } catch {
    // memory only
  }
}

/** Something big got through: the connection is fine again. */
export function markConnectionFine(gameId: string, storage: StorageLike = defaultStorage()): void {
  slowMemory.delete(gameId);
  try {
    storage.removeItem(SLOW_PREFIX + gameId);
  } catch {
    // nothing more to do
  }
}

/** True while this phone's connection is still being treated as slow. */
export function isConnectionSlow(gameId: string, now: number = Date.now(), storage: StorageLike = defaultStorage()): boolean {
  let until = slowMemory.get(gameId) ?? 0;
  try {
    const stored = Number(storage.getItem(SLOW_PREFIX + gameId));
    if (Number.isFinite(stored) && stored > until) until = stored;
  } catch {
    // memory only
  }
  return until > now;
}

/**
 * A round payload with its photos taken out and marked as "a photo will follow". The host reads
 * `photosDeferred` to know it should wait for the photo rather than assume there is none.
 */
export function deferPayloadPhotos(payload: unknown): { payload: unknown; photos: string[] } {
  if (typeof payload !== 'object' || payload === null) return { payload, photos: [] };
  const { photos, ...rest } = payload as Record<string, unknown>;
  const held = Array.isArray(photos) ? photos.filter((p): p is string => typeof p === 'string') : [];
  return held.length > 0 ? { payload: { ...rest, photosDeferred: true }, photos: held } : { payload, photos: [] };
}

// ---------------------------------------------------------------------------
// The guest's view of held-back photos
// ---------------------------------------------------------------------------

export interface FetchedRoundPhotos {
  recordedAt: number;
  photos: string[];
}

/** True when the host held photos back and this guest has not loaded all of them yet. */
export function photosStillHeld(state: SharedGameState, loaded: ReadonlyMap<number, FetchedRoundPhotos>): number[] {
  return (state.photoRounds ?? []).filter((index) => {
    const round = state.roundHistory?.find((r) => r.index === index);
    const have = loaded.get(index);
    return !round || !have || have.recordedAt !== round.recordedAt;
  });
}

/**
 * The state with the fetched photos put back on their rounds. Each is matched on index AND
 * `recordedAt` and ignored otherwise, so a photo can never land on a different round that reused the index.
 */
export function withFetchedPhotos(state: SharedGameState, loaded: ReadonlyMap<number, FetchedRoundPhotos>): SharedGameState {
  if (!state.roundHistory || loaded.size === 0) return state;
  return {
    ...state,
    roundHistory: state.roundHistory.map((r) => {
      const have = loaded.get(r.index);
      return have && have.recordedAt === r.recordedAt && !(r.photos?.length) ? { ...r, photos: have.photos } : r;
    }),
  };
}

/** When a guest's link stops working: an hour after the game ended. Undefined while the game is still on. */
export function guestLinkDeadline(state: Pick<SharedGameState, 'ended' | 'endedAt'>): number | undefined {
  return state.ended && state.endedAt !== undefined ? state.endedAt + GUEST_LINK_WINDOW_MS : undefined;
}

const clock = (ms: number): string => new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/** The sentence that tells a guest how long their link lasts — same wording wherever it appears. */
export function guestLinkWindowNote(state: Pick<SharedGameState, 'ended' | 'endedAt'>): string {
  const deadline = guestLinkDeadline(state);
  return deadline !== undefined
    ? `This link stops working at ${clock(deadline)} — 1 hour after the game ended.`
    : 'This link stops working 1 hour after the game ends.';
}

/** What the guest is told about held-back photos, before and after they have tried to get them. */
export function heldPhotosNote(state: Pick<SharedGameState, 'ended' | 'endedAt'>, missing: number, total: number): string {
  const window = guestLinkWindowNote(state);
  if (total === 0) return '';
  if (missing === 0) return `${total === 1 ? 'The photo has' : `All ${total} photos have`} been added. ${window}`;
  return (
    `Photos were held back because the connection was slow (${missing} of ${total} not loaded yet). ` +
    `Tap Get photos once the connection is better to add them to the round list and the PDF. ${window}`
  );
}
